import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["container", "subtaskContainer", "addTaskButton"]

  connect() {
    this.initializeSortable()
    // Delay subtask initialization to ensure DOM is ready
    setTimeout(() => {
      this.initializeSubtaskSortables()
    }, 100)
    
    // Check if we should auto-open the Add Task modal
    this.checkForAutoOpenNewTask()
  }
  
  checkForAutoOpenNewTask() {
    const urlParams = new URLSearchParams(window.location.search)
    const openNewTask = urlParams.get('open_new_task')
    const parentTaskId = urlParams.get('parent_task_id')
    
    if (openNewTask === 'true') {
      // Remove the parameters from URL to avoid reopening on future page loads
      const url = new URL(window.location)
      url.searchParams.delete('open_new_task')
      url.searchParams.delete('parent_task_id')
      window.history.replaceState({}, '', url)
      
      // Get the new task URL from the button's form action
      const modalFrame = document.getElementById('modal')
      if (modalFrame && this.hasAddTaskButtonTarget) {
        const form = this.addTaskButtonTarget.closest('form')
        if (form) {
          // Build the modal URL, including parent_task_id if present
          let modalUrl = form.action
          if (parentTaskId) {
            modalUrl += `?parent_task_id=${parentTaskId}`
          }
          // Setting src on the turbo-frame triggers Turbo to fetch and display the modal
          modalFrame.src = modalUrl
        }
      }
    }
  }

  initializeSortable() {
    if (this.hasContainerTarget) {
      this.sortable = Sortable.create(this.containerTarget, {
        animation: 150,
        handle: '.procedure__task-header',
        filter: '.procedure__task-add, .procedure__task-header-actions', // Exclude the "Add Task" button and action buttons from triggering drag
        preventOnFilter: false,
        onEnd: this.handleSortEnd.bind(this)
      })
    }
  }

  initializeSubtaskSortables() {
    if (this.hasSubtaskContainerTarget) {
      this.subtaskSortables = []
      this.subtaskContainerTargets.forEach(container => {
        const parentTaskId = container.dataset.parentTaskId
        const sortable = Sortable.create(container, {
          animation: 150,
          handle: '.procedure__task-header',
          filter: '.procedure__task-header-actions',
          preventOnFilter: false,
          group: `subtasks-${parentTaskId}`, // Isolate subtasks within their parent
          onEnd: (event) => this.handleSubtaskSortEnd(event)
        })
        this.subtaskSortables.push(sortable)
      })
    }
  }

  // Method to initialize sortable for subtask containers
  initializeSubtaskSortable(container, parentTaskId) {
    return Sortable.create(container, {
      animation: 150,
      handle: '.procedure__task-header',
      filter: '.procedure__task-header-actions',
      preventOnFilter: false,
      group: `subtasks-${parentTaskId}`, // Isolate subtasks within their parent
      onEnd: (event) => this.handleSubtaskSortEnd(event)
    })
  }

  async handleSortEnd(event) {
    // Only process if the moved item is actually a task (not the "Add Task" button)
    if (event.item.classList.contains('procedure__task-add')) {
      return
    }

    if (event.oldIndex === event.newIndex) {
      return
    }

    const taskElements = Array.from(this.containerTarget.children).filter(element =>
      element.classList.contains('procedure__task')
    )
    const newIndex = taskElements.indexOf(event.item)
    if (newIndex === -1) {
      return
    }

    const taskId = event.item.dataset.taskId
    const newPosition = newIndex + 1

    await this.patchTaskPosition(taskId, newPosition)
  }

  async handleSubtaskSortEnd(event) {
    if (event.oldIndex === event.newIndex) {
      return
    }

    const taskElements = Array.from(event.to.children).filter(element =>
      element.classList.contains('procedure__task')
    )
    const newIndex = taskElements.indexOf(event.item)
    if (newIndex === -1) {
      return
    }

    const taskId = event.item.dataset.taskId
    const newPosition = newIndex + 1

    await this.patchTaskPosition(taskId, newPosition)
  }

  async patchTaskPosition(taskId, position) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]').content

      const response = await fetch(`/procedure_tasks/${taskId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          procedure_task: { position: position }
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      console.error("Error updating task position:", error)
    }
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
    if (this.subtaskSortables) {
      this.subtaskSortables.forEach(sortable => sortable.destroy())
    }
  }
} 